<ng-container *ngIf="serverError">
  <div class="modal-header">
    <h4 class="modal-title">Server Error: {{ serverError }}</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>
</ng-container>

<ng-container *ngIf="!serverError">

  <div class="modal-header">
    <h4 class="modal-title">Invite to Roster</h4>
    <button type="button" class="close" aria-label="Close" (click)="activeModal.dismiss()">
      <span aria-hidden="true">&times;</span>
    </button>
  </div>

  <div class="modal-body pb-0">

    <div class="alert alert-danger text-center mb-3" role="alert"
         *ngIf="errorMessage">
      {{ errorMessage }}
    </div>

    <p>
      <b>{{ eventTypeName }}</b>
    </p>

    <hr>

    <div class="form-group">
      <label for="selectedRoster">Roster</label>
      <select class="custom-select"
              id="selectedRoster"
              name="selectedRoster"
              *ngIf="!loadingRosters"
              [disabled]="hasFixedRoster || loadingSubmit"
              [class.disabled]="hasFixedRoster || loadingSubmit"
              [(ngModel)]="selectedRoster"
              (ngModelChange)="handleRosterChange()">
        <option [ngValue]="null" disabled>
          - Select a Roster -
        </option>
        <option *ngFor="let roster of rosters"
                [ngValue]="roster">
          {{ roster.name }}
        </option>
      </select>
      <select class="custom-select disabled" disabled
              *ngIf="loadingRosters">
        <option>
          Loading...
        </option>
      </select>
    </div>

    <div class="form-group">
      <label for="searchCustomersInput">
        Participant
      </label>
      <input type="text" class="form-control" id="searchCustomersInput"
             placeholder="Search by email, name, barcode, or telephone"
             [(ngModel)]="search"
             (ngModelChange)="handleCustomerFilterChange()">
    </div>

    <!-- Add a fixed-height div to prevent the input from shifting as results are loaded -->
    <div class="mt-3 mb-3" style="height: 231px;overflow-y: auto"
         *ngIf="(selectedCustomer === null) && hasSearchInput()">

      <ng-container *ngIf="hasSearchInput()">

        <table class="table table-bordered table-hover">
          <tbody *ngIf="!customerResultsPresent()">
            <tr>
              <td colspan="99">
                {{ loadingCustomers ? 'Loading...' : 'No results' }}
              </td>
            </tr>
          </tbody>
          <tbody *ngIf="customerResultsPresent()">
            <tr style="cursor:pointer"
                *ngFor="let customer of customerList"
                (click)="selectCustomer(customer)">
              <td>
                {{ customer.first_name }} {{ customer.last_name }}
                <span class="text-muted">
                  {{ dateStringFromISO8601(customer.birthday )}}
                  <span *ngIf="customer.email">- {{ customer.email }}</span>
                </span>
              </td>
            </tr>
          </tbody>
        </table>

        <div class="alert alert-info" *ngIf="!loadingCustomers && hasRecentActivityFilter()">
          Only recently-active customers are being shown.
          <a href="#" (click)="$event.preventDefault(); handleExpandSearch();">
            Expand search to all customers.
          </a>
        </div>

      </ng-container>

    </div>

    <div class="alert alert-info" role="alert"
         *ngIf="existingPendingEntry && selectedCustomer">
      <b>{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }}</b>
      already has a pending invitation to this roster, so sending this will
      invite them again. The start date and booking deadline below are filled
      in from that invitation, so submitting without editing them leaves both
      as they are.
    </div>

    <div class="alert alert-warning" role="alert"
         *ngIf="prefillLookupFailed">
      We could not check whether this participant already has a pending
      invitation to this roster. If they do, sending this will replace its
      start date and booking deadline with whatever is entered below.
    </div>

    <div class="mb-3">
      <label for="startDate">Start Date (optional)</label>
      <app-date-picker [id]="'startDate'"
                       [(content)]="startDate"></app-date-picker>
      <small class="form-text text-muted">
        If this is left blank, the customer chooses a start date when they purchase, if the
        membership allows that. Otherwise the roster membership starts on the day it is purchased.
      </small>
      <app-roster-invite-first-bill [rosterId]="selectedRoster ? selectedRoster.id : null"
                                    [startDate]="startDate"></app-roster-invite-first-bill>
    </div>

    <app-invite-expiry-input [idPrefix]="'inviteToRoster'"
                             [(expiry)]="expiry"
                             [timeZone]="expiryTimeZone()"
                             [disabled]="loadingSubmit"></app-invite-expiry-input>

    <div class="form-group">
      <label>Invite Note (optional)</label>
      <app-markdown-editor [id]="'inviteNote'"
                           [(content)]="inviteNote"
                           [shortEditor]="true"></app-markdown-editor>
      <small class="form-text text-muted">
        This text will be included in the invite, underneath information about the event and (if configured above) the booking deadline.
      </small>
    </div>

    <div class="form-group">
      <div class="form-check"
           [ngbTooltip]="customerBelowMinAge ? 'Required because this participant is below the minimum age to own a membership' : null">
        <input class="form-check-input" type="checkbox"
               id="sendInviteToParentsAndGuardians"
               name="sendInviteToParentsAndGuardians"
               [disabled]="loadingSubmit || customerBelowMinAge"
               [class.disabled]="loadingSubmit || customerBelowMinAge"
               [(ngModel)]="sendInviteToParentsAndGuardians">
        <label class="form-check-label"
               for="sendInviteToParentsAndGuardians">
          Send invite to linked parents &amp; guardians
        </label>
      </div>
      <small class="form-text text-muted">
        If selected, the invite will be sent to any linked parents and guardians of the participant to accept or reject the invite on behalf of the participant. This is in addition to the participant's own email address.
      </small>
      <div class="alert alert-danger mt-2" role="alert"
           *ngIf="customerBelowMinAge && customerHasLinkedAccountsOfSufficientAge === false && !loadingRelations">
        This participant is too young to own a membership, but has no linked accounts of sufficient age to accept this invite.
        Please add a linked account of sufficient age on their customer profile before sending this invite.
      </div>
    </div>

    <div class="alert alert-danger" role="alert"
         *ngIf="hasNoEmailRecipient() && !loadingRelations">
      <b>{{ selectedCustomer.first_name }} {{ selectedCustomer.last_name }}</b> has no
      email address on file, so <b>no email will actually be sent</b>. The
      invitation will still be recorded, and they can still be booked in by
      staff, but they will not be notified.
    </div>

  </div>

  <div class="modal-footer">
    <button type="button" class="btn btn-secondary"
            (click)="activeModal.close()">
      Cancel
    </button>
    <button class="btn btn-primary"
            *ngIf="!loadingSubmit"
            [disabled]="!submitButtonAvailable()"
            [class.disabled]="!submitButtonAvailable()"
            (click)="$event.preventDefault();handleSubmit();">
      Send Invite
    </button>
    <button type="button" class="btn btn-primary disabled" disabled
            *ngIf="loadingSubmit">
      Loading...
    </button>
  </div>

</ng-container>
